/**
 * Adapted from React Bits' FadeContent component.
 * https://reactbits.dev/animations/fade-content
 *
 * React Bits is licensed under MIT + Commons Clause. This local copy keeps the
 * original GSAP choreography while adding a reduced-motion path for the app.
 */
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

interface FadeContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  blur?: boolean;
  duration?: number;
  ease?: string;
  delay?: number;
  threshold?: number;
  initialOpacity?: number;
  /** Route entrances start immediately, independent of scroll position. */
  playOnMount?: boolean;
}

const toSeconds = (value: number) => (value > 10 ? value / 1000 : value);

const FadeContent: React.FC<FadeContentProps> = ({
  children,
  blur = false,
  duration = 700,
  ease = 'power2.out',
  delay = 0,
  threshold = 0.08,
  initialOpacity = 0,
  playOnMount = false,
  className = '',
  style,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) {
      gsap.set(element, {
        autoAlpha: 1,
        clearProps: 'willChange,filter,transform',
      });
      return;
    }

    gsap.set(element, {
      autoAlpha: initialOpacity,
      filter: blur ? 'blur(8px)' : 'blur(0px)',
      y: 10,
      willChange: 'opacity, filter, transform',
    });

    const animation = gsap.to(element, {
      autoAlpha: 1,
      filter: 'blur(0px)',
      y: 0,
      duration: toSeconds(duration),
      delay: toSeconds(delay),
      ease,
      paused: true,
      onComplete: () => {
        gsap.set(element, { clearProps: 'willChange,filter,transform' });
      },
    });

    if (!playOnMount) gsap.registerPlugin(ScrollTrigger);
    const trigger = playOnMount
      ? null
      : ScrollTrigger.create({
          trigger: element,
          start: `top ${(1 - threshold) * 100}%`,
          once: true,
          onEnter: () => animation.play(),
        });
    if (playOnMount) animation.play();

    // Switching the OS preference during an entrance must reveal it at once.
    const onMotionChange = () => {
      if (!motion.matches) return;
      trigger?.kill();
      animation.kill();
      gsap.set(element, {
        autoAlpha: 1,
        clearProps: 'willChange,filter,transform',
      });
    };
    motion.addEventListener('change', onMotionChange);

    return () => {
      motion.removeEventListener('change', onMotionChange);
      trigger?.kill();
      animation.kill();
      gsap.set(element, { clearProps: 'willChange,filter,transform' });
    };
  }, [blur, delay, duration, ease, initialOpacity, threshold, playOnMount]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ visibility: 'hidden', ...style }}
      {...props}
    >
      {children}
    </div>
  );
};

export default FadeContent;
